import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { IconButton } from '~/components/isolated/IconButton';

import bgImage from './assets/bg.png';

interface ModalClientCardProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
  isFullHeight?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export const ModalClientCard: React.FC<ModalClientCardProps> = ({
  isFullHeight = false,
  isOpen,
  onClose,
  ...props
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      {...props}
      className={cn('fixed inset-0 z-50 flex items-end justify-center')}
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        role="presentation"
      />

      <div
        className={cn(
          'relative w-full max-w-lg rounded-t-6.5 bg-center bg-cover bg-image-cover bg-pink-200',
          isFullHeight ? 'h-full' : 'h-[calc(50vh+20%)]',
        )}
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="flex items-center justify-end p-4">
          <IconButton
            size="S"
            variant="outline"
            icon="close"
            onClick={onClose}
            aria-label={t('close')}
          />
        </div>

        <div className="flex flex-col items-center gap-4 px-6 pt-2 pb-safe-offset-6">
          <div className="flex aspect-308/165 w-full items-center justify-center rounded-5 border border-stroke-quaternary bg-graphic-regular-white">
            <Typography.Text200Medium className="text-text-secondary">
              barcode
            </Typography.Text200Medium>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
